import { doc, getDoc } from "firebase/firestore";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { db } from "@/firebase/app";
import { legacyFallbackAppearance, makeAppearance, type SBotAppearance } from "@/models/appearance";
import { parseLeague, type LeagueModel } from "@/models/catalog";
import { parseOrganization, type OrganizationModel } from "@/models/organization";
import { activeMembership, resolvedLeagueID, type SBotUser } from "@/models/user";

// Port of SBotAppearanceService.resolveAppearance: organization colors win
// only for organization workspaces, with league colors as their fallback.
// The result is published as CSS variables so every component and map layer
// picks up the active workspace's branding.

const ThemeContext = createContext<SBotAppearance>(makeAppearance());

export interface ThemeDocumentLoader {
  league(leagueID: string): Promise<LeagueModel | null>;
  organization(organizationID: string): Promise<OrganizationModel | null>;
}

export function contextOrganizationID(user: SBotUser): string | undefined {
  const context = user.activeContext;
  if (context?.targetType !== "organization") return undefined;
  return context.organizationID ?? context.targetID;
}

export function contextLeagueID(user: SBotUser): string | undefined {
  const context = user.activeContext;
  if (context?.leagueID) return context.leagueID;
  if (context?.targetType === "league") return context.targetID;
  const membership = activeMembership(user);
  return membership ? resolvedLeagueID(membership) : undefined;
}

const firestoreThemeDocumentLoader: ThemeDocumentLoader = {
  async league(leagueID) {
    const snapshot = await getDoc(doc(db, "leagues", leagueID));
    return snapshot.exists() ? parseLeague(snapshot.id, snapshot.data()) : null;
  },
  async organization(organizationID) {
    const snapshot = await getDoc(doc(db, "organizations", organizationID));
    return snapshot.exists() ? parseOrganization(snapshot.id, snapshot.data()) : null;
  },
};

async function leagueAppearance(
  leagueID: string,
  fallback: SBotAppearance,
  loader: ThemeDocumentLoader,
): Promise<SBotAppearance | null> {
  try {
    const league = await loader.league(leagueID);
    if (!league || (!league.primaryColorHex && !league.secondaryColorHex)) return null;
    return makeAppearance(
      league.name,
      league.primaryColorHex ?? fallback.primaryColorHex,
      league.secondaryColorHex ?? fallback.secondaryColorHex,
      "league",
    );
  } catch {
    return null;
  }
}

export async function resolveAppearance(
  user: SBotUser | null,
  loader: ThemeDocumentLoader = firestoreThemeDocumentLoader,
): Promise<SBotAppearance> {
  const fallback = legacyFallbackAppearance(user);
  if (!user) return fallback;

  const organizationID = contextOrganizationID(user);
  if (organizationID) {
    try {
      const organization = await loader.organization(organizationID);
      if (organization) {
        const leagueFallback = (await leagueAppearance(organization.leagueID, fallback, loader)) ?? fallback;
        if (organization.primaryColorHex || organization.secondaryColorHex) {
          return makeAppearance(
            organization.name,
            organization.primaryColorHex ?? leagueFallback.primaryColorHex,
            organization.secondaryColorHex ?? leagueFallback.secondaryColorHex,
            "organization",
          );
        }
        return leagueFallback;
      }
    } catch {
      return fallback;
    }
  }

  const leagueID = contextLeagueID(user);
  if (leagueID) {
    const appearance = await leagueAppearance(leagueID, fallback, loader);
    if (appearance) return appearance;
  }

  return fallback;
}

export function themeContextKey(user: SBotUser | null): string {
  const context = user?.activeContext;
  return [
    user?.id,
    context?.targetType,
    context?.targetID,
    context?.role,
    user ? contextLeagueID(user) : undefined,
    user ? contextOrganizationID(user) : undefined,
  ].join("|");
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useAuth();
  const [appearance, setAppearance] = useState<SBotAppearance>(() => makeAppearance());
  const contextKey = themeContextKey(currentUser);

  useEffect(() => {
    let cancelled = false;
    setAppearance(legacyFallbackAppearance(currentUser));
    void resolveAppearance(currentUser).then((resolved) => {
      if (!cancelled) setAppearance(resolved);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--sbot-primary", `#${appearance.primaryColorHex}`);
    root.style.setProperty("--sbot-secondary", `#${appearance.secondaryColorHex}`);
  }, [appearance]);

  return <ThemeContext.Provider value={appearance}>{children}</ThemeContext.Provider>;
}

export function useAppearance(): SBotAppearance {
  return useContext(ThemeContext);
}
